import {useFormik} from 'formik'
import {useCallback, useEffect, useMemo, useState} from 'react'
import * as yup from 'yup'
import {LocationModel} from '../../../../../../../models/acs/LocationModel'
import {BookingProductListModel} from '../../../../../../../models/ems/ProductModel'
import {TicketModelFulfillParams} from '../../../../../../../models/ems/TicketModel'
import {GetLocationByCode, GetProductLocationsByCode} from '../../../../../acs/redux/AcsCRUD'
import {ApiTree} from '../../../../../../../utils/Tree/ApiTree'
import {useAlerts} from '../../../../../../../components/alerts/useAlerts'
import {useOnChange} from '../../../../../../../components/hooks/useOnChange'
import {usePrevious} from '../../../../../../../components/hooks/usePrevious'
import {useSafeStateUpdate} from '../../../../../../../components/hooks/useSafeStateUpdate'
import {useSeatMapState} from '../../../../../../../components/hooks/useSeatMapState'
import {SeatMapSelectionModalInput} from '../../../../../../../components/inputs/SeatMapInput/SeatMapSelectionModalInput'
import {SeatMapValue} from '../../../../../../../components/inputs/SeatMapInput/SeatMapValue'
import {TreeNodeItem} from '../../../../../../../components/inputs/TreeSelect/TreeSelect'
import {TableActionModalProps} from '../../../../../../../components/hooks/useTableModalActions'
import {DateRange} from '../../../../../../../utils/DateRange'
import {DateUtil} from '../../../../../../../utils/DateUtil'
import moment from 'moment'

export interface SeatMapSelectionModalFormValues {
  selected: SeatMapValue
  locationCode: string
  dateRange?: DateRange
}

export interface SeatMapSelectionModalFormProps extends TableActionModalProps {
  initialValues?: SeatMapSelectionModalFormValues
  onSubmit: (values: SeatMapSelectionModalFormValues) => void | Promise<void>
  product?: BookingProductListModel | null
}

export const SeatMapSelectionModalForm = ({
  onSubmit,
  initialValues = EMPTY_FORM_VALUES,
  onClose,
  isOpen,
  product,
}: SeatMapSelectionModalFormProps) => {
  const [count, setCount] = useState(0)
  const {
    resetOccupiedData,
    seatMapSpacingX,
    occupied,
    extra,
    disabled,
    columns,
    rows,
    hidden,
    isRightToLeft,
    isBottomToTop,
    resetState: resetSeatMapState,
    isLoading,
    setIsLoading,
  } = useSeatMapState()
  const safeUpdate = useSafeStateUpdate()
  const previousInitialValues = usePrevious(initialValues)
  const previousOpen = usePrevious(isOpen)
  const {push, pushError} = useAlerts()
  const [locations, setLocations] = useState<LocationModel[]>([])

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      await onSubmit(values)
    },
  })

  const handleSeatMapChange = useCallback(
    (value: SeatMapValue) => {
      formik.setFieldValue('selected', value)
    },
    [formik]
  )

  const resetLocationByCode = useCallback(
    async (locationCode: string, eventCode: string) => {
      const doneLoading = setIsLoading(locationCode)
      try {
        const {data} = await GetLocationByCode(locationCode)
        if (!data.seatMap) {
          push({
            message: `No seat maps available for ${data.name}`,
            variant: 'danger',
            timeout: 5000,
          })
        } else {
          safeUpdate(() => resetSeatMapState(data.seatMap))
        }
      } catch (e) {
        pushError(e)
      } finally {
        if (product) {
          resetOccupiedData(locationCode, eventCode, product?.code)
        }
        doneLoading()
      }
    },
    [setIsLoading, push, safeUpdate, resetSeatMapState, pushError, product, resetOccupiedData]
  )

  const locationItems = useMemo((): TreeNodeItem[] => {
    const locationTree = new ApiTree(locations)
    return locationTree.getTreeSelectItems()
  }, [locations])

  const handleLocationChange = useCallback(
    (locationCode) => {
      formik.setFieldValue('locationCode', locationCode)
      formik.setFieldValue('selected', new SeatMapValue())
      resetSeatMapState()
      if (locationCode && product) {
        resetLocationByCode(locationCode, product.eventCode)
      }
    },
    [formik, product, resetLocationByCode, resetSeatMapState]
  )

  const resetLocationList = useCallback(async () => {
    setLocations([])
    if (product) {
      const {data} = await GetProductLocationsByCode(product.code)
      setLocations(data)
    }
  }, [product])

  useOnChange(product, () => {
    if (product) {
      setCount(product.bookingProductQty || 0)
      resetLocationList()
    }
  })

  useEffect(() => {
    if (previousInitialValues !== initialValues) {
      formik.resetForm()
    }
  }, [formik, initialValues, previousInitialValues])

  useEffect(() => {
    if (previousOpen !== isOpen) {
      if (isOpen) {
        formik.setValues(initialValues)
      } else {
        formik.setValues(EMPTY_FORM_VALUES)
      }
      resetSeatMapState()
    }
  }, [formik, initialValues, isOpen, previousOpen, resetSeatMapState])

  const getModalTitle = useCallback(() => {
    return product?.name || ''
  }, [product?.name])

  const handleDateRangeChange = useCallback(
    (value: DateRange) => {
      formik.setFieldValue('dateRange', value)
    },
    [formik]
  )
  const minDate = useMemo(() => {
    const today = moment().startOf('day')
    if (product) {
      const startedAt = DateUtil.getDateFromApiString(product.startedAt)
      return moment.max(moment(startedAt), today).toDate()
    }
    return today.toDate()
  }, [product])

  const maxDate = useMemo(() => {
    if (product) {
      return DateUtil.getDateFromApiString(product.endedAt)
    }
  }, [product])

  const handleSubmit = useCallback(() => {
    if (!formik.isValid && formik.errors.dateRange) {
      push({
        message: formik.errors.dateRange,
        timeout: 5000,
        variant: 'danger',
      })
    }
    formik.submitForm()
  }, [formik, push])

  return (
    <>
      <SeatMapSelectionModalInput
        modalTitle={getModalTitle}
        spacingX={seatMapSpacingX}
        occupied={occupied}
        extra={extra}
        locationItems={locationItems}
        locationCode={formik.values.locationCode}
        onLocationChange={handleLocationChange}
        onSubmit={handleSubmit}
        count={count}
        disabled={disabled}
        onDateRangeChange={handleDateRangeChange}
        dateRange={product?.isTimeslot ? formik.values.dateRange : undefined}
        columns={columns}
        rows={rows}
        loading={isLoading}
        disableSubmit={formik.isSubmitting || !formik.isValid}
        disableSelection={formik.isSubmitting}
        onChange={handleSeatMapChange}
        value={formik.values.selected}
        open={isOpen}
        hidden={hidden}
        onHide={onClose}
        isRightToLeft={isRightToLeft}
        isBottomToTop={isBottomToTop}
        minDate={minDate}
        maxDate={maxDate}
      />
    </>
  )
}

const EMPTY_FORM_VALUES: SeatMapSelectionModalFormValues = {
  selected: new SeatMapValue(),
  locationCode: '',
  dateRange: new DateRange(),
}

export const getPayload = (
  values: SeatMapSelectionModalFormValues,
  productCode: string
): TicketModelFulfillParams => {
  const payload: TicketModelFulfillParams = {
    locationCode: values.locationCode,
    seats: values.selected.getSeatMapObject(),
    productCode,
  }

  return payload
}

const validationSchema = yup.object().shape({
  dateRange: yup.mixed().test('date-is-valid', 'Invalid date selected', (value) => {
    const dateRange = value as DateRange
    return (
      dateRange.hasValues() &&
      dateRange.getStartOrFail().valueOf() !== dateRange.getEndOrFail().valueOf()
    )
  }),
})
